import type { Rule } from '../../core/assetType.js';
import type { ValidationIssue } from '../../core/issues.js';
import type { Gameshow } from '../../schemas/gameshow.js';

import { toPointer } from '../../core/issues.js';

const EXPECTED_CHARACTER_COUNT = 2;

export const characters: Rule<Gameshow> = {
  code: 'gameshow.characters',
  check(input): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const chars = input.characters;

    if (chars.length !== EXPECTED_CHARACTER_COUNT) {
      const path: (string | number)[] = ['characters'];
      issues.push({
        code: 'gameshow.characters.count',
        severity: 'error',
        message: `Gameshow must have exactly ${EXPECTED_CHARACTER_COUNT} characters (player + contestant), found ${chars.length}. The host is the narrator, not a character.`,
        path,
        pointer: toPointer(path),
        currentValue: chars.length,
        constraint: { kind: 'range', detail: `exactly ${EXPECTED_CHARACTER_COUNT}` },
      });
    }

    const player = chars.find(c => c.role === 'self');
    const contestant = chars.find(c => c.role === 'rival');
    const host = chars.find(c => c.role === 'manager');

    if (!player) {
      const path: (string | number)[] = ['characters'];
      issues.push({
        code: 'gameshow.characters.missing-player',
        severity: 'error',
        message: 'No character with role \'self\' (the player).',
        path,
        pointer: toPointer(path),
        constraint: { kind: 'custom', detail: 'one character must have role \'self\'' },
      });
    }
    else {
      const idx = chars.indexOf(player);
      if (player.name !== 'You') {
        const path: (string | number)[] = ['characters', idx, 'name'];
        issues.push({
          code: 'gameshow.characters.player-name',
          severity: 'error',
          message: `Player character must be named 'You', found '${player.name}'.`,
          path,
          pointer: toPointer(path),
          locator: { characterId: player.id },
          currentValue: player.name,
          constraint: { kind: 'enum', allowedValues: ['You'] },
        });
      }
      if (player.id !== 'you') {
        const path: (string | number)[] = ['characters', idx, 'id'];
        issues.push({
          code: 'gameshow.characters.player-id',
          severity: 'error',
          message: `Player character must have id 'you', found '${player.id}'.`,
          path,
          pointer: toPointer(path),
          locator: { characterId: player.id },
          currentValue: player.id,
          constraint: { kind: 'enum', allowedValues: ['you'] },
        });
      }
    }

    if (!contestant) {
      const path: (string | number)[] = ['characters'];
      issues.push({
        code: 'gameshow.characters.missing-contestant',
        severity: 'error',
        message: 'No character with role \'rival\' (the contestant).',
        path,
        pointer: toPointer(path),
        constraint: { kind: 'custom', detail: 'one character must have role \'rival\'' },
      });
    }

    if (host) {
      const idx = chars.indexOf(host);
      const path: (string | number)[] = ['characters', idx, 'role'];
      issues.push({
        code: 'gameshow.characters.host-as-character',
        severity: 'error',
        message: `Character '${host.name || host.id}' has role 'manager' — the host must be a narrator, not a character. Remove this entry and use top-level narratorName / narratorContentAssetSlug.`,
        path,
        pointer: toPointer(path),
        locator: { characterId: host.id },
        currentValue: 'manager',
        constraint: { kind: 'enum', allowedValues: ['self', 'rival'] },
      });
    }

    chars.forEach((char, idx) => {
      const raw = char as unknown as Record<string, unknown>;
      if (raw.startingScore !== undefined) {
        const path: (string | number)[] = ['characters', idx, 'startingScore'];
        issues.push({
          code: 'gameshow.characters.starting-score-forbidden',
          severity: 'error',
          message: `Character '${char.name || char.id}' has 'startingScore' — remove it; score state lives in scoreDefinitions.`,
          path,
          pointer: toPointer(path),
          locator: { characterId: char.id },
          currentValue: raw.startingScore,
          constraint: { kind: 'custom', detail: 'startingScore not allowed on characters' },
        });
      }
    });

    return issues;
  },
};
